import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import Navigation from '../components/Navigation'
import LoginForm from '../components/LoginForm'
import { useAuth } from '../hooks/useAuth'
import { apiClient, RecentScore } from '../services/apiClient'

export default function HomePage() {
  const { isAuthenticated } = useAuth()
  const [scores, setScores] = useState<RecentScore[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchRecentScores = async () => {
      try {
        setLoading(true)
        setError(null)
        const recentScores = await apiClient.getRecentScores(5)
        setScores(recentScores)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load recent scores')
        console.error('Error fetching recent scores:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchRecentScores()
  }, [])

  return (
    <div className="min-h-screen bg-firedup-dark">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content Area */}
          <div className="lg:col-span-2">
            {/* Hero Section */}
            <div className="mb-12 text-center">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
                TAKE YOUR GAMING TO
                <span className="block bg-gradient-to-r from-firedup-orange to-firedup-red bg-clip-text text-transparent">
                  THE NEXT LEVEL
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Track your scores, compete with players worldwide, and climb the leaderboards across multiple locations and games.
              </p>
              
              {!isAuthenticated && (
                <div className="text-center">
                  <Link 
                    to="/register" 
                    className="btn-primary inline-block text-lg px-8 py-4"
                  >
                    SIGN UP TO GET STARTED
                  </Link>
                </div>
              )}
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FeatureCard 
                title="Live Leaderboards"
                description="Real-time rankings across all gaming locations and titles"
                icon="🏆"
              />
              <FeatureCard 
                title="Score Tracking"
                description="Score recording and personal achievement history for integrated games"
                icon="📊"
              />
              <FeatureCard 
                title="Multi-Location"
                description="Compete across the globe in arcades, gaming lounges, and tournaments"
                icon="🎮"
              />
            </div>

            {/* Recent High Scores / Public Content */}
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-white mb-6">Recent High Scores</h2>
              <div className="bg-firedup-gray/50 backdrop-blur-sm rounded-lg border border-firedup-light-gray overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-firedup-light-gray">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Player</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Game</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Score</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Location</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-firedup-light-gray">
                      {loading ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                            Loading recent scores...
                          </td>
                        </tr>
                      ) : error ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-red-400">
                            Error: {error}
                          </td>
                        </tr>
                      ) : scores.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                            No recent scores available
                          </td>
                        </tr>
                      ) : (
                        scores.map((score, index) => (
                          <ScoreRow 
                            key={`${score.playerName}-${score.date}-${index}`}
                            player={score.playerName} 
                            game={score.data.gameType || 'Unknown Game'} 
                            score={score.score.toLocaleString()} 
                            location={score.location} 
                            date={score.date} 
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* <div className="text-center mt-6">
                <Link 
                  to="/leaderboards" 
                  className="btn-secondary inline-block"
                >
                  View All Leaderboards
                </Link>
              </div> */}
            </div>
          </div>

          {/* Sidebar - Login Form for non-authenticated users */}
          <div className="lg:col-span-1">
            {!isAuthenticated ? (
              <div className="sticky top-24">
                <LoginForm />
              </div>
            ) : (
              <div className="sticky top-24">
                <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
                  <h3 className="text-xl font-bold text-white mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <Link to="/dashboard" className="w-full btn-secondary text-left block">
                      📊 View Dashboard
                    </Link>
                    <Link to="/locations" className="w-full btn-secondary text-left block">
                      🏢 Find Locations
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface FeatureCardProps {
  title: string
  description: string
  icon: string
}

function FeatureCard({ title, description, icon }: FeatureCardProps) {
  return (
    <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray hover:border-firedup-orange transition-colors duration-300">
      <div className="text-3xl mb-4">{icon}</div>
      <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
      <p className="text-gray-300">{description}</p>
    </div>
  )
}

interface ScoreRowProps {
  player: string
  game: string
  score: string
  location: string
  date: string
}

function ScoreRow({ player, game, score, location, date }: ScoreRowProps) {
  return (
    <tr className="hover:bg-firedup-light-gray/50 transition-colors duration-200">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-gradient-to-r from-firedup-orange to-firedup-red rounded-full flex items-center justify-center mr-3">
            <span className="text-white text-xs font-bold">
              {player.charAt(0)}
            </span>
          </div>
          <span className="text-white font-medium">{player}</span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-gray-300">{game}</td>
      <td className="px-6 py-4 whitespace-nowrap text-firedup-orange font-bold">{score}</td>
      <td className="px-6 py-4 whitespace-nowrap text-gray-300">{location}</td>
      <td className="px-6 py-4 whitespace-nowrap text-gray-400 text-sm">{date}</td>
    </tr>
  )
}
